import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Calendar, Clock, Video, X, ArrowLeft, ArrowRight, Check, AlertTriangle, RotateCcw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function ManageBookingPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('view'); // view | reschedule | cancel | done
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [doneMessage, setDoneMessage] = useState('');

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  useEffect(() => {
    if (!token) {
      setError('No booking token provided');
      setLoading(false);
      return;
    }
    axios.get(`${API_URL}/api/bookings/manage/${token}`)
      .then((res) => setBooking(res.data))
      .catch((e) => setError(e.response?.data?.detail || 'Could not load booking'))
      .finally(() => setLoading(false));
  }, [token]);

  const formatTime = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const hour = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${hour}:${String(m).padStart(2, '0')} ${period}`;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return { firstDay: new Date(year, month, 1).getDay(), daysInMonth: new Date(year, month + 1, 0).getDate(), year, month };
  };

  const handleDateClick = async (day) => {
    const { year, month } = getDaysInMonth(calendarMonth);
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(dateStr);
    setSelectedSlot(null);
    setLoadingSlots(true);
    try {
      const res = await axios.get(`${API_URL}/api/booking-slots/${dateStr}`);
      setSlots(res.data.slots || []);
    } catch {
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleCancel = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/bookings/manage/${token}/cancel`);
      setDoneMessage('Your session has been cancelled. We sent you a confirmation email.');
      setMode('done');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not cancel session');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReschedule = async () => {
    if (!selectedDate || !selectedSlot) {
      toast.error('Pick a new date and time first');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/bookings/manage/${token}/reschedule`, {
        date: selectedDate,
        time: selectedSlot,
      });
      setBooking({ ...booking, date: res.data.date, time: res.data.time, zoom_join_url: res.data.zoom_join_url, zoom_password: res.data.zoom_password });
      setDoneMessage(`Your session has been moved to ${res.data.date} at ${formatTime(res.data.time)}. We sent you an updated email.`);
      setMode('done');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not reschedule');
    } finally {
      setSubmitting(false);
    }
  };

  const { firstDay, daysInMonth, year, month } = getDaysInMonth(calendarMonth);

  if (loading) {
    return (
      <Layout title="Manage Booking">
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title="Manage Booking">
        <div className="bg-[#FAFAFA] min-h-screen flex items-center justify-center">
          <div className="max-w-md mx-auto bg-white p-8 border border-[#E2E8F0] rounded-2xl shadow-lg text-center" data-testid="manage-error">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle size={26} className="text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-[#0A0F1C] mb-2">Link not valid</h2>
            <p className="text-[#475569] mb-5 text-sm">{error}</p>
            <Link to="/schedule" className="btn-primary-blue inline-block">Book a new session</Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Manage Your Booking" description="Reschedule or cancel your FREE 20-minute complimentary business jumpstart session.">
      <div className="bg-[#FAFAFA] min-h-screen" data-testid="manage-booking-page">
        <section className="bg-gradient-to-br from-[#0F172A] via-[#1E1B4B] to-[#0F172A] text-white py-12">
          <div className="container-custom text-center">
            <h1 className="text-3xl sm:text-4xl font-bold mb-2" data-testid="manage-title">Manage Your Session</h1>
            <p className="text-[#94A3B8]">Reschedule or cancel anytime - no questions asked.</p>
          </div>
        </section>

        <section className="container-custom py-10">
          <div className="max-w-xl mx-auto bg-white border border-[#E2E8F0] rounded-2xl shadow-lg shadow-slate-100/80 p-6 sm:p-8 -mt-6 relative z-10">
            {/* Current booking summary */}
            <div className="bg-gradient-to-r from-[#EFF6FF] to-[#F0F9FF] border border-[#DBEAFE] p-4 rounded-xl mb-5">
              <div className="text-[10px] font-bold text-[#1D4ED8] tracking-wider uppercase mb-2">Current Session</div>
              <div className="flex items-center gap-2 mb-1.5"><Calendar size={14} className="text-[#1D4ED8]" /><span className="text-sm font-semibold text-[#0A0F1C]" data-testid="current-date">{new Date(booking.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</span></div>
              <div className="flex items-center gap-2 mb-1.5"><Clock size={14} className="text-[#1D4ED8]" /><span className="text-sm font-semibold text-[#0A0F1C]" data-testid="current-time">{formatTime(booking.time)}</span></div>
              {booking.zoom_join_url && (
                <div className="flex items-start gap-2 mt-2"><Video size={14} className="text-[#1D4ED8] mt-0.5" /><a href={booking.zoom_join_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#1D4ED8] hover:underline break-all">{booking.zoom_join_url}</a></div>
              )}
              <div className="text-xs text-[#94A3B8] mt-2">Booked under: {booking.name} &middot; {booking.email}</div>
            </div>

            {/* View mode */}
            {mode === 'view' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="manage-actions">
                <Button onClick={() => setMode('reschedule')} className="btn-primary-blue w-full" data-testid="open-reschedule-btn">
                  <RotateCcw size={14} className="mr-2" /> Reschedule
                </Button>
                <Button onClick={() => setMode('cancel')} className="bg-white border border-red-200 text-red-600 hover:bg-red-50 w-full font-semibold" data-testid="open-cancel-btn">
                  <X size={14} className="mr-2" /> Cancel Session
                </Button>
              </div>
            )}

            {/* Cancel confirmation */}
            {mode === 'cancel' && (
              <div data-testid="cancel-step">
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-bold text-red-900 text-sm">Are you sure?</div>
                      <p className="text-sm text-red-800 mt-1">This will release your time slot and cancel your Zoom meeting. You can always rebook later.</p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button onClick={() => setMode('view')} className="btn-outline flex-1" disabled={submitting}><ArrowLeft size={14} className="mr-2" /> Keep my session</Button>
                  <Button onClick={handleCancel} disabled={submitting} className="bg-red-600 hover:bg-red-700 text-white flex-1" data-testid="confirm-cancel-btn">
                    {submitting ? 'Cancelling...' : 'Yes, cancel'}
                  </Button>
                </div>
              </div>
            )}

            {/* Reschedule flow */}
            {mode === 'reschedule' && (
              <div data-testid="reschedule-step">
                <button onClick={() => { setMode('view'); setSelectedDate(null); setSelectedSlot(null); }} className="text-sm text-[#1D4ED8] mb-4 flex items-center gap-1 font-medium hover:underline" data-testid="reschedule-back-btn"><ArrowLeft size={14} /> Back</button>
                <h3 className="font-bold text-[#0A0F1C] text-base mb-1">Pick a new date</h3>
                <p className="text-xs text-[#94A3B8] mb-3">Closed Sundays &amp; 2nd Saturdays · 30-min slots</p>
                <div className="border border-[#E2E8F0] rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <button onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1))} className="p-1.5 hover:bg-[#F1F5F9] rounded"><ArrowLeft size={16} /></button>
                    <h4 className="font-bold text-[#0A0F1C] text-sm">{calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h4>
                    <button onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1))} className="p-1.5 hover:bg-[#F1F5F9] rounded"><ArrowRight size={16} /></button>
                  </div>
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {dayNames.map(d => <div key={d} className="text-center text-[10px] font-medium text-[#94A3B8] py-1">{d}</div>)}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {Array.from({ length: firstDay }, (_, i) => <div key={`e-${i}`}></div>)}
                    {Array.from({ length: daysInMonth }, (_, i) => {
                      const day = i + 1;
                      const dateObj = new Date(year, month, day);
                      const isPast = dateObj < today;
                      const dow = dateObj.getDay();
                      const isSunday = dow === 0;
                      const isSecondSaturday = dow === 6 && day >= 8 && day <= 14;
                      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                      const isSelected = selectedDate === dateStr;
                      const isDisabled = isPast || isSunday || isSecondSaturday;
                      return (
                        <button
                          key={day}
                          onClick={() => !isDisabled && handleDateClick(day)}
                          disabled={isDisabled}
                          className={`w-9 h-9 rounded-lg text-sm font-medium transition-all mx-auto flex items-center justify-center ${isSelected ? 'bg-[#1D4ED8] text-white' : isDisabled ? 'text-[#D1D5DB] cursor-not-allowed' : 'hover:bg-[#EFF6FF] text-[#0A0F1C]'}`}
                          data-testid={`r-cal-day-${day}`}
                        >
                          {day}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {selectedDate && (
                  <div className="mb-4">
                    <h4 className="font-bold text-[#0A0F1C] text-sm mb-2">Available times</h4>
                    {loadingSlots ? (
                      <div className="py-4 text-center"><div className="w-6 h-6 border-3 border-[#1D4ED8] border-t-transparent rounded-full animate-spin mx-auto"></div></div>
                    ) : slots.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2">
                        {slots.map((slot, i) => (
                          <button
                            key={slot.start}
                            onClick={() => setSelectedSlot(slot.start)}
                            className={`py-2 px-3 text-xs rounded-lg border transition-all font-medium ${selectedSlot === slot.start ? 'bg-[#1D4ED8] text-white border-[#1D4ED8]' : 'border-[#E2E8F0] text-[#475569] hover:border-[#1D4ED8] hover:text-[#1D4ED8]'}`}
                            data-testid={`r-time-${i}`}
                          >
                            {formatTime(slot.start)}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-[#475569] text-center py-3">No slots available on this date</p>
                    )}
                  </div>
                )}

                {selectedDate && selectedSlot && (
                  <Button onClick={handleReschedule} disabled={submitting} className="btn-primary-blue w-full" data-testid="confirm-reschedule-btn">
                    {submitting ? 'Updating...' : `Confirm new time: ${selectedDate} at ${formatTime(selectedSlot)}`}
                  </Button>
                )}
              </div>
            )}

            {/* Done */}
            {mode === 'done' && (
              <div className="text-center py-2" data-testid="manage-done">
                <div className="w-14 h-14 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 ring-4 ring-green-50">
                  <Check size={24} className="text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-[#0A0F1C] mb-2">All set</h2>
                <p className="text-[#475569] mb-5 text-sm">{doneMessage}</p>
                <Link to="/" className="btn-outline inline-block">Back to home</Link>
              </div>
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
}
